import { useGetFounderApprovals } from "@workspace/api-client-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { Check, X, Eye, Shield } from "lucide-react";
import InfoTip from "@/components/info-tip";

const itemTypeLabels: Record<string, string> = {
  fact: "Fact",
  kpi_change: "KPI Change",
  milestone: "Milestone",
  risk: "Risk",
  ask: "Ask",
  media: "Media",
};

export default function FounderApprovals({ founderId }: { founderId: number }) {
  const { data: items, isLoading, refetch } = useGetFounderApprovals(founderId);
  const { toast } = useToast();

  const handleDecision = async (itemId: number, decision: "approve" | "reject") => {
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/founder/${founderId}/approvals/${itemId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ decision }),
      });
      if (!res.ok) throw new Error("Failed to submit decision");
      toast({
        title: decision === "approve" ? "Approved" : "Rejected",
        description: decision === "approve"
          ? "This item can now be shared with investors"
          : "This item will remain internal",
      });
      refetch();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6" data-testid="page-founder-approvals">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-serif font-bold tracking-tight">Approvals</h1>
          <InfoTip content="Nothing goes to investors without your say-so. The ops team picks key highlights, milestones, and KPIs from your updates and flags them here for you to approve or reject. Approved items become visible to investors in their Intelligence Room." />
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Review items before they are shared with investors. You control what goes external.
        </p>
      </div>

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4 flex items-start gap-3">
          <Shield className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-blue-900">Your approval is required</p>
            <p className="text-xs text-blue-700 mt-1">
              Items below have been reviewed by the Favcy team and are ready for your approval before being shared with investors. You can approve or reject each item.
            </p>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : !items?.length ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <CheckSquareIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p>No items pending your approval</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((item: any) => (
            <Card key={item.id}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="text-xs">
                        {itemTypeLabels[item.type] || item.type}
                      </Badge>
                      <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700">
                        <Eye className="w-3 h-3 mr-1" /> {item.visibility}
                      </Badge>
                    </div>
                    <p className="text-sm">{item.summary}</p>
                    {item.confidence != null && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Confidence: {Math.round(item.confidence * 100)}%
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDecision(item.id, "reject")}
                    >
                      <X className="w-4 h-4 mr-1" /> Reject
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleDecision(item.id, "approve")}
                    >
                      <Check className="w-4 h-4 mr-1" /> Approve
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function CheckSquareIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="m9 11 3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  );
}
